<template>
    <div>
        <FormLabel 
            v-if="!props.hideLabel"
            :for="props.id" 
            :required="props.required"
        >{{ props.label }}</FormLabel>

        <div 
            :id="props.id"
            @drop.prevent="handleDrop"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @click="triggerFileInput"
            :class="[
                'tw-relative tw-w-full tw-min-h-[120px] tw-border-2 tw-border-dashed tw-rounded-lg tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer tw-transition-all tw-duration-200',
                isDragging ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-gray-300 hover:tw-border-primary-400',
                props.invalid && '!tw-border-red-500',
                props.disabled && '!tw-bg-gray-100 !tw-cursor-not-allowed'
            ]"
        >
            <!-- File Preview -->
            <div v-if="previewUrl" class="tw-relative tw-w-full tw-h-full tw-min-h-[120px] tw-flex tw-items-center tw-justify-center">
                <img 
                    v-if="isImage" 
                    :src="previewUrl" 
                    :alt="props.label"
                    class="tw-max-w-full tw-max-h-[200px] tw-object-contain tw-rounded"
                />
                <div v-else class="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-p-4">
                    <icon icon="solar:file-bold-duotone" class="tw-text-4xl tw-text-gray-400" />
                    <span class="tw-text-sm tw-text-gray-600">{{ fileName }}</span>
                </div>
                
                <!-- Remove button -->
                <button 
                    @click.stop="removeFile"
                    type="button"
                    class="tw-absolute tw-top-2 tw-right-2 tw-w-6 tw-h-6 tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-red-600 tw-transition-colors"
                >
                    <icon icon="solar:close-circle-bold" class="tw-text-sm" />
                </button>
            </div>

            <!-- Upload Area -->
            <div v-else class="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-p-6">
                <icon 
                    :icon="props.leftIcon || 'solar:upload-bold-duotone'" 
                    :class="[
                        'tw-text-3xl tw-text-gray-400',
                        props.invalid && '!tw-text-red-500'
                    ]"
                />
                <div class="tw-text-center">
                    <p class="tw-text-sm tw-font-medium tw-text-gray-700">
                        {{ props.placeholder || 'Click to upload or drag and drop' }}
                    </p>
                    <p class="tw-text-xs tw-text-gray-500 tw-mt-1">
                        {{ props.help || 'PNG, JPG, GIF up to 10MB' }}
                    </p>
                </div>
            </div>

            <!-- Hidden file input -->
            <input
                ref="fileInput"
                type="file"
                :accept="props.accept"
                :multiple="props.multiple"
                :disabled="props.disabled"
                @change="handleFileSelect"
                class="tw-hidden"
            />
        </div>

        <FormErrorMessage v-if="!props.hideErrors">{{ props.error }}</FormErrorMessage>
    </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import FormLabel from './FormLabel.vue';
import FormErrorMessage from './FormErrorMessage.vue';

const props = defineProps({
    id: String,
    label: String,
    required: Boolean,
    error: String,
    help: String,
    placeholder: String,
    modelValue: [File, String],
    leftIcon: String,
    accept: {
        type: String,
        default: 'image/*'
    },
    multiple: {
        type: Boolean,
        default: false
    },
    disabled: Boolean,
    hideLabel: {
        type: Boolean,
        default: false
    },
    hideErrors: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue']);

const fileInput = ref(null);
const isDragging = ref(false);
const previewUrl = ref('');
const fileName = ref('');

const isImage = computed(() => {
    if (!props.modelValue) return false;
    if (props.modelValue instanceof File) {
        return props.modelValue.type.startsWith('image/');
    }
    return true; // Assume string values are image URLs
});

const triggerFileInput = () => {
    if (!props.disabled) {
        fileInput.value?.click();
    }
};

const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
        processFile(file);
    }
};

const handleDrop = (event) => {
    isDragging.value = false;
    const file = event.dataTransfer.files?.[0];
    if (file) {
        processFile(file);
    }
};

const processFile = (file) => {
    // Validate file type
    if (props.accept && props.accept !== '*/*') {
        const acceptedTypes = props.accept.split(',').map(type => type.trim());
        const isValidType = acceptedTypes.some(type => {
            if (type.endsWith('/*')) {
                return file.type.startsWith(type.replace('/*', '/'));
            }
            return file.type === type;
        });
        
        if (!isValidType) {
            emit('update:modelValue', null);
            return;
        }
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
        emit('update:modelValue', null);
        return;
    }

    fileName.value = file.name;
    
    if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
            previewUrl.value = e.target.result;
        };
        reader.readAsDataURL(file);
    }

    emit('update:modelValue', file);
};

const removeFile = () => {
    previewUrl.value = '';
    fileName.value = '';
    if (fileInput.value) {
        fileInput.value.value = '';
    }
    emit('update:modelValue', null);
};

// Watch for external modelValue changes
watch(() => props.modelValue, (newValue) => {
    if (!newValue) {
        previewUrl.value = '';
        fileName.value = '';
    } else if (typeof newValue === 'string') {
        previewUrl.value = newValue;
        fileName.value = 'Uploaded file';
    }
}, { immediate: true });
</script>

<style scoped>
/* Custom scrollbar for file preview */
::-webkit-scrollbar {
    width: 6px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}
</style> 